import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { client } from "@/data/client";
import { MultiSeriesChart } from "@/components/MultiSeriesChart";
import { FunnelViz } from "@/components/FunnelViz";
import { formatPct } from "@/lib/utils";
import { playPop } from "@/lib/sound";
import { SdrPerformance } from "@/pages/dashboard-sections/SdrPerformance";
import { CallDaySection } from "@/pages/dashboard-sections/CallDaySection";
import { SdrStageSection } from "@/pages/dashboard-sections/SdrStageSection";
import type { SdrStageKey } from "@/data/types";

type Period = { periodStart: string; periodEnd: string };

const SERIES = [
  { key: "leads", name: "Leads", color: "hsl(var(--chart-4))" },
  { key: "connections", name: "Conexões", color: "hsl(var(--chart-3))" },
  { key: "meetingsScheduled", name: "Reuniões agendadas", color: "hsl(var(--chart-1))" },
  { key: "meetingsHeld", name: "Reuniões realizadas", color: "hsl(var(--chart-2))" },
];

export function SdrFunnelTab({ period }: { period: Period }) {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedStage, setSelectedStage] = useState<SdrStageKey | null>(null);

  const kpis = useQuery({ queryKey: ["sdr-funnel-kpis", period], queryFn: () => client.getSdrFunnelKpis(period) });
  const daily = useQuery({ queryKey: ["sdr-funnel-daily", period], queryFn: () => client.getSdrFunnelDaily(period) });

  const k = kpis.data;

  const handleChartClick = (payload: Record<string, unknown>) => {
    const date = payload?.date;
    if (typeof date === "string") {
      setSelectedDay(date);
      setSelectedStage(null);
    }
  };

  const handleStageClick = (stage: SdrStageKey) => {
    playPop();
    setSelectedStage(stage);
    setSelectedDay(null);
  };

  return (
    <div className="space-y-6">
      {/* Funil + stage section lado a lado. Quando nada está selecionado o
          funil ocupa 100% da largura; ao clicar numa banda o grid transiciona
          suavemente pra 2fr / 3fr e a section entra deslizando da direita. */}
      <div
        className="grid gap-5"
        style={{
          gridTemplateColumns: selectedStage
            ? "minmax(0, 2fr) minmax(0, 3fr)"
            : "minmax(0, 1fr)",
          transition: "grid-template-columns 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div className="min-w-0 min-h-0">
          <FunnelViz<SdrStageKey>
            title="Funil de prospecção"
            description="Prospecção e qualificação · cada estágio mostra a taxa de conversão para o próximo · clique para ver os contatos"
            stages={[
              { key: "leads", label: "Leads", value: k?.leads ?? 0, caption: "entrantes no período", color: "var(--chart-4)" },
              { key: "connections", label: "Conexões", value: k?.connections ?? 0, caption: "contatos com resposta", color: "var(--chart-3)" },
              { key: "meetingsScheduled", label: "Reuniões agendadas", value: k?.meetingsScheduled ?? 0, caption: "calendário confirmado", color: "var(--chart-1)" },
              { key: "meetingsHeld", label: "Reuniões realizadas", value: k?.meetingsHeld ?? 0, caption: "closer assumiu", color: "var(--chart-2)" },
            ]}
            rates={[
              k ? formatPct(k.leadsToConnectionsRate, 1) : "—",
              k ? formatPct(k.connectionsToScheduledRate, 1) : "—",
              k ? formatPct(k.scheduledToHeldRate, 1) : "—",
            ]}
            activeStage={selectedStage}
            onStageClick={handleStageClick}
          />
        </div>

        <AnimatePresence mode="wait">
          {selectedStage && (
            <motion.div
              key={selectedStage}
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 28 }}
              transition={{ duration: 0.35, ease: [0.215, 0.61, 0.355, 1] }}
              className="min-w-0"
            >
              <SdrStageSection
                stage={selectedStage}
                period={period}
                onClear={() => setSelectedStage(null)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <MultiSeriesChart
        data={daily.data ?? []}
        series={SERIES}
        title="Visão geral"
        subtitle="Evolução do funil de prospecção por dia · clique num dia para ver as ligações realizadas"
        defaultMode="line"
        allowModeToggle={false}
        onPointClick={handleChartClick}
        pulseAllSeries
      />

      <CallDaySection date={selectedDay} onClear={() => setSelectedDay(null)} />

      <SdrPerformance period={period} />
    </div>
  );
}
